/**
 * DESIGN SYSTEM STEP 2 — the print side of the printer upgrade.
 *
 * - `cachedImage`: each decoration is embedded ONCE per PDF and reused by every
 *   page that draws it (before: a sticker on 9 pages was stored 9 times).
 * - `elementTransformOps` / `withElementTransform`: rotation + flip for EVERY
 *   element type, composed like the editor's `rotate() scaleX() scaleY()`.
 * - `embedGrayMask` / `embedJpgWithSoftMask`: a photo keeps its JPEG bytes and
 *   gets a grey alpha plane beside it (/SMask) — a torn edge without turning
 *   the photo into a 5 MB PNG.
 * - `drawPageBackground`, `drawElementShadow`: the paper and the soft shadow,
 *   with the numbers from decorStyle.ts (the editor reads the same ones).
 */
import {
  PDFDocument, PDFImage, PDFName, PDFPage, PDFRawStream, PDFRef, type Color,
  concatTransformationMatrix, popGraphicsState, pushGraphicsState,
} from 'pdf-lib';
import { SHADOW, shadowable, type DecorElement } from './decorStyle';
import { blurAlpha } from './gaussianAlpha';
import { tintPixels, type TintStops } from './tint';

const MM_TO_PT = 72 / 25.4;

// ── one embed per document ──────────────────────────────────────────────────
const docCaches = new WeakMap<PDFDocument, Map<string, Promise<unknown>>>();

/** Memoise an embed (or any per-document object) by key for this document.
 *  The promise is cached, so two pages asking at once still embed once. A
 *  failed embed is forgotten so a later page can retry. */
export function cachedImage<T = PDFImage>(doc: PDFDocument, key: string, make: () => Promise<T>): Promise<T> {
  let cache = docCaches.get(doc);
  if (!cache) { cache = new Map(); docCaches.set(doc, cache); }
  const hit = cache.get(key) as Promise<T> | undefined;
  if (hit) return hit;
  const p = make();
  const c = cache;
  c.set(key, p);
  p.catch(() => { if (c.get(key) === p) c.delete(key); });
  return p;
}

/** The one cache key of a decoration FILE, whatever draws it: a paper that is a
 *  page's background on one page and a layer under the photos on another is ONE
 *  embed (design system v2 §10 « each piece stored once per PDF » — the Valencia
 *  check found the 2700 × 3600 papers stored twice, ~1.5 MB each, 2026-10-14). */
export const pieceKey = (url: string): string => `piece|${url}`;

// ── rotation + flip ─────────────────────────────────────────────────────────
/** A box in pdf space: (x, y) = bottom-left corner, y up. */
export interface BoxPt { x: number; y: number; w: number; h: number }
export interface ElementXf { rotation?: number; flipH?: boolean; flipV?: boolean }
type Matrix = [number, number, number, number, number, number];

/** m · n — apply n first, then m (PDF matrices [a b c d e f]). */
function mul(m: Matrix, n: Matrix): Matrix {
  return [
    m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

/** The matrix that turns an element drawn flat in `box` into the editor's
 *  `rotate(r) scaleX(±1) scaleY(±1)` about the box centre. CSS rotation is
 *  clockwise on a y-down screen; pdf space is y-up, so the angle is negated.
 *  `pre` (a page-space shift, e.g. the shadow offset) is applied last. */
export function elementTransformOps(box: BoxPt, t: ElementXf, pre: { dx: number; dy: number } = { dx: 0, dy: 0 }): { matrix: Matrix; identity: boolean } {
  const rot = t.rotation ?? 0;
  const sx = t.flipH ? -1 : 1;
  const sy = t.flipV ? -1 : 1;
  const identity = rot === 0 && sx === 1 && sy === 1 && pre.dx === 0 && pre.dy === 0;
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  const th = (-rot * Math.PI) / 180;
  const cos = Math.cos(th);
  const sin = Math.sin(th);
  let m: Matrix = [1, 0, 0, 1, cx + pre.dx, cy + pre.dy];
  m = mul(m, [cos, sin, -sin, cos, 0, 0]);
  m = mul(m, [sx, 0, 0, sy, 0, 0]);
  m = mul(m, [1, 0, 0, 1, -cx, -cy]);
  return { matrix: m, identity };
}

/** Run `draw` inside the element's transform (graphics state saved/restored). */
export async function withElementTransform(
  page: PDFPage, box: BoxPt, t: ElementXf,
  draw: () => Promise<void> | void,
  pre?: { dx: number; dy: number },
): Promise<void> {
  const { matrix, identity } = elementTransformOps(box, t, pre);
  page.pushOperators(pushGraphicsState());
  if (!identity) page.pushOperators(concatTransformationMatrix(...matrix));
  try {
    await draw();
  } finally {
    page.pushOperators(popGraphicsState());
  }
}

// ── soft masks ──────────────────────────────────────────────────────────────
/** A DeviceGray 8-bit image (0 = transparent … 255 = opaque), Flate-packed. */
export function embedGrayMask(doc: PDFDocument, gray: Uint8Array, w: number, h: number): PDFRef {
  const stream = doc.context.flateStream(gray, {
    Type: 'XObject', Subtype: 'Image', Width: w, Height: h,
    ColorSpace: 'DeviceGray', BitsPerComponent: 8,
  });
  return doc.context.register(stream);
}

/** Embed a JPEG and give it `mask` as /SMask. The mask may have any pixel size
 *  — PDF maps both onto the same unit square. Not cached: a masked photo is its
 *  own XObject. */
export async function embedJpgWithSoftMask(doc: PDFDocument, jpg: Uint8Array, mask: PDFRef): Promise<PDFImage> {
  const img = await doc.embedJpg(jpg);
  await img.embed(); // write the XObject now so its dictionary can be edited
  const stream = doc.context.lookup(img.ref) as PDFRawStream;
  stream.dict.set(PDFName.of('SMask'), mask);
  return img;
}

// ── browser-only (canvas) ───────────────────────────────────────────────────
export type FetchBlob = (url: string) => Promise<Blob>;

/** The grey plane of an image's alpha, stretched to w×h. */
export async function alphaPlane(url: string, w: number, h: number, fetchBlob: FetchBlob): Promise<Uint8Array> {
  const bmp = await createImageBitmap(await fetchBlob(url));
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  const d = ctx.getImageData(0, 0, w, h).data;
  const out = new Uint8Array(w * h);
  for (let i = 0; i < out.length; i++) out[i] = d[i * 4 + 3];
  return out;
}

/**
 * A TINTED piece for the print (lib/decor/tint.ts): its colour map decoded, recoloured with
 * the editor's matrix (tintPixels), re-encoded — PNG (alpha kept) for a piece, JPEG for a
 * paper. The caller caches it per file and tint (tintKey).
 */
async function tintedCanvas(blob: Blob, stops: TintStops): Promise<HTMLCanvasElement> {
  const bmp = await createImageBitmap(blob, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  const c = document.createElement('canvas');
  c.width = bmp.width; c.height = bmp.height;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  const id = ctx.getImageData(0, 0, c.width, c.height);
  tintPixels(id.data, stops);
  ctx.putImageData(id, 0, 0);
  return c;
}
export async function tintedPiecePng(blob: Blob, stops: TintStops): Promise<Uint8Array> {
  const c = await tintedCanvas(blob, stops);
  const out = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/png'));
  if (!out) throw new Error('tint raster failed');
  return new Uint8Array(await out.arrayBuffer());
}
export async function tintedPieceJpg(blob: Blob, stops: TintStops): Promise<Uint8Array> {
  const c = await tintedCanvas(blob, stops);
  const out = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/jpeg', 0.92));
  if (!out) throw new Error('tint raster failed');
  return new Uint8Array(await out.arrayBuffer());
}

/** Paper: colour fill and/or image cover-fitted to the full bleed (media)
 *  rect, embedded once per document. The caller clips to the media rect. */
export async function drawPageBackground(
  doc: PDFDocument, page: PDFPage,
  bg: { imageUrl?: string | null; color?: string | null },
  media: { x: number; y: number; w: number; h: number },
  deps: { hexToRgb: (hex: string) => Color; loadImage: (url: string) => Promise<PDFImage> },
): Promise<void> {
  if (bg.color) page.drawRectangle({ x: media.x, y: media.y, width: media.w, height: media.h, color: deps.hexToRgb(bg.color) });
  const url = bg.imageUrl;
  if (!url) return;
  const img = await cachedImage(doc, pieceKey(url), () => deps.loadImage(url));
  const s = Math.max(media.w / img.width, media.h / img.height);
  const dw = img.width * s;
  const dh = img.height * s;
  page.drawImage(img, { x: media.x + (media.w - dw) / 2, y: media.y + (media.h - dh) / 2, width: dw, height: dh });
}

/** Shadow raster resolution — it is blurred, 150 dpi is plenty. */
const SHADOW_DPI = 150;

function hexChannels(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

/** The soft shadow of one element: its silhouette (icon alpha contain-fitted,
 *  photo mask stretched, or the photo's plain box), blurred by SHADOW.sigmaMm,
 *  tinted SHADOW.color, drawn at SHADOW.opacity, shifted in page space, under
 *  the element's own rotation/flip. One PNG per (silhouette, size), cached. */
export async function drawElementShadow(
  doc: PDFDocument, page: PDFPage,
  el: DecorElement & ElementXf,
  box: BoxPt,
  fetchBlob: FetchBlob,
): Promise<void> {
  if (!shadowable(el)) return;
  const sigmaPt = SHADOW.sigmaMm * MM_TO_PT;
  const padPt = Math.ceil(3 * sigmaPt);
  const pxPerPt = SHADOW_DPI / 72;
  const silhouette = el.type === 'icon' ? `icon|${el.fit ?? 'contain'}|${el.imageUrl}` : el.maskUrl ? `mask|${el.maskUrl}` : 'box';
  const key = `shadow|${silhouette}|${Math.round(box.w)}x${Math.round(box.h)}`;
  const img = await cachedImage(doc, key, async () => {
    const W = Math.max(4, Math.round((box.w + 2 * padPt) * pxPerPt));
    const H = Math.max(4, Math.round((box.h + 2 * padPt) * pxPerPt));
    const pad = padPt * pxPerPt;
    const bw = box.w * pxPerPt;
    const bh = box.h * pxPerPt;
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const ctx = c.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    if (el.type === 'icon' && el.imageUrl) {
      const bmp = await createImageBitmap(await fetchBlob(el.imageUrl));
      const fit = Math.min(bw / bmp.width, bh / bmp.height);
      const dw = el.fit === 'fill' ? bw : bmp.width * fit;
      const dh = el.fit === 'fill' ? bh : bmp.height * fit;
      ctx.drawImage(bmp, pad + (bw - dw) / 2, pad + (bh - dh) / 2, dw, dh);
      bmp.close();
    } else if (el.maskUrl) {
      const bmp = await createImageBitmap(await fetchBlob(el.maskUrl));
      ctx.drawImage(bmp, pad, pad, bw, bh);
      bmp.close();
    } else {
      ctx.fillStyle = '#000';
      ctx.fillRect(pad, pad, bw, bh);
    }
    const id = ctx.getImageData(0, 0, W, H);
    const alpha = new Uint8ClampedArray(W * H);
    for (let i = 0; i < alpha.length; i++) alpha[i] = id.data[i * 4 + 3];
    const blurred = blurAlpha(alpha, W, H, sigmaPt * pxPerPt);
    const [r, g, b] = hexChannels(SHADOW.color);
    for (let i = 0; i < blurred.length; i++) {
      id.data[i * 4] = r; id.data[i * 4 + 1] = g; id.data[i * 4 + 2] = b; id.data[i * 4 + 3] = blurred[i];
    }
    ctx.putImageData(id, 0, 0);
    const png = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/png'));
    if (!png) throw new Error('shadow raster failed');
    return doc.embedPng(new Uint8Array(await png.arrayBuffer()));
  });
  // Down on the page = −y in pdf space.
  const pre = { dx: SHADOW.offsetXMm * MM_TO_PT, dy: -SHADOW.offsetYMm * MM_TO_PT };
  await withElementTransform(page, box, el, () => {
    page.drawImage(img, { x: box.x - padPt, y: box.y - padPt, width: box.w + 2 * padPt, height: box.h + 2 * padPt, opacity: SHADOW.opacity });
  }, pre);
}
